//src/components/SellPropertyContext.js
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axiosInstance from '../axios';
import { createEmptyNearbyPlace, initialForm, STEPS } from './sellProperty.constants';
import { buildPayload, formatRupiahInput, validateStep } from './sellProperty.utils';

const SellPropertyContext = createContext(null);

export const useSellProperty = () => {
    const ctx = useContext(SellPropertyContext);
    if (!ctx) {
        throw new Error('useSellProperty must be used inside SellPropertyProvider');
    }
    return ctx;
};

export const SellPropertyProvider = ({ children }) => {
    const navigate = useNavigate();

    const [step, setStep] = useState(0);
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [propertyTypes, setPropertyTypes] = useState([]);
    const [certificateTypes, setCertificateTypes] = useState([]);
    const [featureGroups, setFeatureGroups] = useState([]);
    const [loadingData, setLoadingData] = useState(true);

    const [propertyImages, setPropertyImages] = useState([]);
    const [imageError, setImageError] = useState('');

    const setFieldValue = useCallback((name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    }, []);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('mp_user') || '{}');
        if (user?.name || user?.phone || user?.phone_number) {
            setForm((prev) => ({
                ...prev,
                ownerName: user.name || '',
                ownerPhone: user.phone || user.phone_number || '',
            }));
        }
    }, []);

    useEffect(() => {
        const fetchRefData = async () => {
            try {
                const [typesRes, certRes, groupsRes] = await Promise.all([
                    axiosInstance.get('/master-data/property-types'),
                    axiosInstance.get('/master-data/property-certificate-types'),
                    axiosInstance.get('/master-data/property-feature-groups'),
                ]);

                if (typesRes.data?.success) {
                    setPropertyTypes(Array.isArray(typesRes.data.data) ? typesRes.data.data : []);
                }

                if (certRes.data?.success) {
                    setCertificateTypes(Array.isArray(certRes.data.data) ? certRes.data.data : []);
                }

                const groups = Array.isArray(groupsRes.data?.data) ? groupsRes.data.data : [];

                const groupsWithFeatures = await Promise.all(
                    groups.map(async (group) => {
                        try {
                            const featuresRes = await axiosInstance.get(
                                `/master-data/property-feature-groups/${group.code}/features`
                            );

                            const features = Array.isArray(featuresRes.data?.data?.features)
                                ? featuresRes.data.data.features
                                : [];

                            return { ...group, features };
                        } catch (err) {
                            return { ...group, features: [] };
                        }
                    })
                );

                setFeatureGroups(groupsWithFeatures);
            } catch (err) {
                console.error('Failed to load reference data:', err);

                if (err.response?.status === 401) {
                    navigate('/login', {
                        state: {
                            from: '/sell-property',
                            message: 'Silakan login terlebih dahulu.',
                        },
                    });
                }
            } finally {
                setLoadingData(false);
            }
        };

        fetchRefData();
    }, [navigate]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    }, []);

    const handlePriceChange = useCallback((e) => {
        const raw = e.target.value;
        const formatted = formatRupiahInput(raw);

        setForm((prev) => ({ ...prev, price: formatted }));
        setErrors((prev) => ({ ...prev, price: '' }));
    }, []);

    const handleToggleFeature = useCallback((featureId) => {
        const normalizedId = Number(featureId);

        setForm((prev) => {
            const current = Array.isArray(prev.feature_ids) ? prev.feature_ids : [];
            const exists = current.some((id) => Number(id) === normalizedId);

            return {
                ...prev,
                feature_ids: exists ? current.filter((id) => Number(id) !== normalizedId) : [...current, normalizedId],
            };
        });
    }, []);

    const isFeatureSelected = useCallback(
        (featureId) => {
            const normalizedId = Number(featureId);
            const current = Array.isArray(form.feature_ids) ? form.feature_ids : [];
            return current.some((id) => Number(id) === normalizedId);
        },
        [form.feature_ids]
    );

    const addNearbyPlace = useCallback(() => {
        setForm((prev) => {
            const current = Array.isArray(prev.nearby_places) ? prev.nearby_places : [];
            return {
                ...prev,
                nearby_places: [...current, createEmptyNearbyPlace(current.length)],
            };
        });
    }, []);

    const updateNearbyPlace = useCallback((index, field, value) => {
        setForm((prev) => {
            const current = Array.isArray(prev.nearby_places) ? [...prev.nearby_places] : [];
            if (!current[index]) return prev;

            current[index] = {
                ...current[index],
                [field]: value,
            };

            return {
                ...prev,
                nearby_places: current,
            };
        });
    }, []);

    const removeNearbyPlace = useCallback((index) => {
        setForm((prev) => {
            const current = Array.isArray(prev.nearby_places) ? prev.nearby_places : [];
            const next = current
                .filter((_, i) => i !== index)
                .map((item, idx) => ({
                    ...item,
                    sort_order: idx,
                }));

            return {
                ...prev,
                nearby_places: next,
            };
        });
    }, []);

    const selectedPropertyType = useMemo(
        () => propertyTypes.find((t) => String(t.id) === String(form.property_type_id)),
        [propertyTypes, form.property_type_id]
    );

    const selectedCertificateType = useMemo(
        () => certificateTypes.find((t) => String(t.id) === String(form.certificate_type_id)),
        [certificateTypes, form.certificate_type_id]
    );

    const isTanah = Boolean(selectedPropertyType?.name?.toLowerCase().includes('tanah'));

    const nextStep = useCallback(() => {
        const e = validateStep(step, form);

        if (Object.keys(e).length > 0) {
            setErrors(e);
            return;
        }

        setStep((s) => Math.min(s + 1, STEPS.length - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [step, form]);

    const prevStep = useCallback(() => {
        setStep((s) => Math.max(s - 1, 0));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const resetForm = useCallback(() => {
        setForm(initialForm);
        setStep(0);
        setErrors({});
        setPropertyImages([]);
        setImageError('');
        setSubmitted(false);

        const user = JSON.parse(localStorage.getItem('mp_user') || '{}');
        if (user?.name || user?.phone || user?.phone_number) {
            setForm((prev) => ({
                ...prev,
                ownerName: user.name || '',
                ownerPhone: user.phone || user.phone_number || '',
            }));
        }
    }, []);

    const handleSubmit = useCallback(async () => {
        const token = localStorage.getItem('mp_token');
        const user = localStorage.getItem('mp_user');

        if (!token || !user) {
            navigate('/login', {
                state: {
                    from: '/sell-property',
                    message: 'Silakan login terlebih dahulu untuk mengajukan properti.',
                },
            });
            return;
        }

        setLoading(true);
        setErrors({});

        try {
            await axiosInstance.post('/properties', buildPayload(form, propertyImages), {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Properti berhasil diajukan.',
                confirmButtonText: 'OK',
                showCancelButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
            });

            setSubmitted(true);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem('mp_token');
                localStorage.removeItem('mp_user');

                navigate('/login', {
                    state: {
                        from: '/sell-property',
                        message: 'Sesi login habis. Silakan login ulang.',
                    },
                });
                return;
            }

            const apiErrors = err?.response?.data?.errors;
            if (apiErrors) {
                const mapped = {};
                Object.entries(apiErrors).forEach(([key, msgs]) => {
                    mapped[key] = Array.isArray(msgs) ? msgs[0] : msgs;
                });
                setErrors(mapped);
            } else {
                setErrors({
                    general: err?.response?.data?.message || 'Terjadi kesalahan. Silakan coba lagi.',
                });
            }
        } finally {
            setLoading(false);
        }
    }, [form, propertyImages, navigate]);

    return (
        <SellPropertyContext.Provider
            value={{
                step,
                form,
                errors,
                loading,
                submitted,
                propertyTypes,
                certificateTypes,
                featureGroups,
                loadingData,
                propertyImages,
                setPropertyImages,
                imageError,
                setImageError,
                handleChange,
                handlePriceChange,
                setFieldValue,
                handleToggleFeature,
                isFeatureSelected,
                addNearbyPlace,
                updateNearbyPlace,
                removeNearbyPlace,
                nextStep,
                prevStep,
                resetForm,
                handleSubmit,
                selectedPropertyType,
                selectedCertificateType,
                isTanah,
            }}
        >
            {children}
        </SellPropertyContext.Provider>
    );
};
